// Types for Emergency Cancellation System

// Urgency levels for emergency cancellations
export type CancellationUrgency = 'immediate' | 'same-day' | 'next-24-hours';

// Reason categories for emergency cancellations
export type CancellationReasonCategory =
  | 'medical_emergency'
  | 'family_emergency'
  | 'mental_health_crisis'
  | 'transportation'
  | 'childcare'
  | 'work_conflict'
  | 'weather'
  | 'illness'
  | 'other';

// Status of the cancellation request
export type CancellationStatus =
  | 'pending'
  | 'acknowledged'
  | 'approved'
  | 'rescheduled'
  | 'denied';

// Emergency cancellation request
export interface EmergencyCancellationRequest {
  id: string;
  appointmentId: string;
  appointmentDate: string;
  appointmentTime: string;
  appointmentType: 'video' | 'in-person';

  // Client info
  clientId: string;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;

  // Provider info
  providerId: string;
  providerName: string;

  // Cancellation details
  urgency: CancellationUrgency;
  reasonCategory: CancellationReasonCategory;
  reasonDetails: string;
  requestedAt: Date;

  // Rescheduling preference
  wantsToReschedule: boolean;
  preferredRescheduleDate?: string;
  preferredRescheduleTime?: string;
  rescheduleNotes?: string;

  // Status tracking
  status: CancellationStatus;
  acknowledgedAt?: Date;
  acknowledgedBy?: string;
  processedAt?: Date;
  processedBy?: string;
  processingNotes?: string;

  // If rescheduled
  newAppointmentId?: string;
  newAppointmentDate?: string;
  newAppointmentTime?: string;

  // If denied
  denialReason?: string;

  // Communication
  notificationSent: boolean;
  notificationSentAt?: Date;
  clientNotified: boolean;
  clientNotifiedAt?: Date;

  // Archive
  archived?: boolean;
  archivedAt?: Date;
}

// Provider notification for emergency cancellation
export interface EmergencyCancellationNotification {
  id: string;
  cancellationRequestId: string;
  providerId: string;
  clientName: string;
  appointmentDate: string;
  appointmentTime: string;
  urgency: CancellationUrgency;
  reasonCategory: CancellationReasonCategory;
  reasonPreview: string;
  timestamp: Date;
  read: boolean;
  readAt?: Date;
  dismissed: boolean;
}

// Form data for submitting a cancellation request
export interface CancellationRequestFormData {
  appointmentId: string;
  urgency: CancellationUrgency;
  reasonCategory: CancellationReasonCategory;
  reasonDetails: string;
  wantsToReschedule: boolean;
  preferredRescheduleDate?: string;
  preferredRescheduleTime?: string;
  rescheduleNotes?: string;
}

// Response when processing a cancellation
export interface ProcessCancellationResponse {
  success: boolean;
  message: string;
  newAppointmentId?: string;
  newAppointmentDate?: string;
  newAppointmentTime?: string;
}

// Statistics for provider dashboard
export interface CancellationStats {
  totalThisMonth: number;
  pendingRequests: number;
  byReason: Partial<Record<CancellationReasonCategory, number>>;
  rescheduledCount: number;
  averageResponseTime: number; // in minutes
}

// Callback types
export type CancellationRequestsCallback = (requests: EmergencyCancellationRequest[]) => void;
export type CancellationNotificationsCallback = (notifications: EmergencyCancellationNotification[]) => void;
export type UnsubscribeFunction = () => void;

// Reason category display labels
export const CANCELLATION_REASON_LABELS: Record<CancellationReasonCategory, string> = {
  medical_emergency: 'Medical Emergency',
  family_emergency: 'Family Emergency',
  mental_health_crisis: 'Mental Health Crisis',
  transportation: 'Transportation Issue',
  childcare: 'Childcare Issue',
  work_conflict: 'Work Conflict',
  weather: 'Severe Weather',
  illness: 'Illness (Non-Emergency)',
  other: 'Other'
};

// Urgency display labels and colors
export const URGENCY_CONFIG: Record<CancellationUrgency, { label: string; color: string; bgColor: string }> = {
  immediate: {
    label: 'Immediate',
    color: '#dc3545',
    bgColor: 'rgba(220, 53, 69, 0.1)'
  },
  'same-day': {
    label: 'Same Day',
    color: '#fd7e14',
    bgColor: 'rgba(253, 126, 20, 0.1)'
  },
  'next-24-hours': {
    label: 'Next 24 Hours',
    color: '#ffc107',
    bgColor: 'rgba(255, 193, 7, 0.1)'
  }
};

// Status display config
export const STATUS_CONFIG: Record<CancellationStatus, { label: string; color: string; bgColor: string }> = {
  pending: {
    label: 'Pending',
    color: '#ffc107',
    bgColor: 'rgba(255, 193, 7, 0.1)'
  },
  acknowledged: {
    label: 'Acknowledged',
    color: '#17a2b8',
    bgColor: 'rgba(23, 162, 184, 0.1)'
  },
  approved: {
    label: 'Approved',
    color: '#28a745',
    bgColor: 'rgba(40, 167, 69, 0.1)'
  },
  rescheduled: {
    label: 'Rescheduled',
    color: '#6f42c1',
    bgColor: 'rgba(111, 66, 193, 0.1)'
  },
  denied: {
    label: 'Denied',
    color: '#dc3545',
    bgColor: 'rgba(220, 53, 69, 0.1)'
  }
};
